const DEFAULT_OG_IMAGE = 'https://skyewiresolutions.com/logo-full.png';

/** Create or update a tag matched by a CSS selector, setting one attribute on it */
const setTag = (selector, createFn, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = createFn();
    document.head.appendChild(el);
  }
  el[attr] = value;
  return el;
};

/**
 * Update meta tags in document head. `ogImage` defaults to the site logo so
 * social shares always carry an image even when a page doesn't pass one.
 */
export const updateMetaTags = (title, description, ogImage = '', canonicalUrl = '') => {
  const image = ogImage || DEFAULT_OG_IMAGE;

  if (document.title !== title) {
    document.title = title;
  }

  setTag('meta[name="description"]', () => {
    const el = document.createElement('meta');
    el.name = 'description';
    return el;
  }, 'content', description);

  setTag('meta[property="og:title"]', () => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:title');
    return el;
  }, 'content', title);

  setTag('meta[property="og:description"]', () => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:description');
    return el;
  }, 'content', description);

  setTag('meta[property="og:image"]', () => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:image');
    return el;
  }, 'content', image);

  setTag('meta[name="twitter:card"]', () => {
    const el = document.createElement('meta');
    el.name = 'twitter:card';
    return el;
  }, 'content', 'summary_large_image');

  setTag('meta[name="twitter:title"]', () => {
    const el = document.createElement('meta');
    el.name = 'twitter:title';
    return el;
  }, 'content', title);

  setTag('meta[name="twitter:description"]', () => {
    const el = document.createElement('meta');
    el.name = 'twitter:description';
    return el;
  }, 'content', description);

  setTag('meta[name="twitter:image"]', () => {
    const el = document.createElement('meta');
    el.name = 'twitter:image';
    return el;
  }, 'content', image);

  if (canonicalUrl) {
    setTag('link[rel="canonical"]', () => {
      const el = document.createElement('link');
      el.rel = 'canonical';
      return el;
    }, 'href', canonicalUrl);
  }
};

/**
 * Set the robots meta tag. Pages that shouldn't be indexed (client-only
 * utility pages, the post-submit thank-you page) pass 'noindex, follow';
 * everything else passes 'index, follow'.
 */
export const setRobotsMeta = (directive = 'index, follow') => {
  setTag('meta[name="robots"]', () => {
    const el = document.createElement('meta');
    el.name = 'robots';
    return el;
  }, 'content', directive);
};

/**
 * Generate page-specific schema based on type. `pageData.pillars` (the same
 * servicePillars array rendered on the Solutions page) drives the ItemList so
 * the schema can't drift out of sync with the on-page tiers.
 */
export const generatePageSchema = (pageType, pageData = {}) => {
  const baseUrl = 'https://skyewiresolutions.com';

  const schemas = {
    home: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Skyewire Solutions - AI Automation & Security for Service Businesses',
      url: `${baseUrl}/`,
      description: 'Enterprise-grade automation and security built for growing service and trade businesses.',
    },
    solutions: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Our Solutions',
      url: `${baseUrl}/solutions`,
      description: 'From free assessment to ongoing partnership. Four-tier service model.',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: (pageData.pillars || []).map((pillar, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: pillar.name,
          description: pillar.description,
        })),
      },
    },
    about: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Skyewire Solutions',
      url: `${baseUrl}/about`,
      description: 'Learn about Spencer McCain and the Skyewire Solutions mission.',
    },
    contact: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Skyewire Solutions',
      url: `${baseUrl}/contact`,
      description: 'Get your free AI Automation & Security Assessment.',
    },
    onboarding: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Client Onboarding',
      url: `${baseUrl}/onboarding`,
      description: 'What to expect and what to prepare when starting a project with Skyewire Solutions.',
    },
    submitRequirements: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Submit Project Requirements',
      url: `${baseUrl}/onboarding/submit_requirements`,
      description: 'Submit your project requirements to Skyewire Solutions.',
    },
    privacyPolicy: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      url: `${baseUrl}/documents/Privacy-Policy`,
      description: 'How Skyewire Solutions collects, uses, and protects your information.',
    },
    thankYou: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Assessment Submitted',
      url: `${baseUrl}/thank-you`,
      description: 'Your assessment has been submitted. Check your email for your personalized report.',
    },
  };

  return schemas[pageType] || schemas.home;
};

/** Build FAQPage schema from an array of { question, answer } pairs */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

/**
 * Inject page-specific JSON-LD schema into document head. Only ever touches
 * the script tagged id="ld-page" - the sitewide business schema in
 * index.html (id="ld-org") is a separate tag and is never removed by this.
 */
export const injectSchema = (schema) => {
  const existingScript = document.getElementById('ld-page');
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'ld-page';
  script.innerHTML = JSON.stringify(schema);
  document.head.appendChild(script);
};
